import {none, some} from '@danwang/option';
import {
  AllFeePeriodsDocument,
  CategorizationConfidence,
  useAllFieldsQuery,
  DepositoryType,
  FeePeriodsByAccountDocument,
  TransferType,
  useAllCategoriesQuery,
  useAttachmentFeePeriodsQuery,
  useCreateFeePeriodMutation,
  useCreateTransactionMutation,
  useLinkBenefitToRawTransactionMutation,
  useSuggestCategorizationsQuery,
  type AttachmentCardBenefitFragment,
  type AttachmentFeePeriodFragment,
  type BasicRawTransactionFragment,
  type CreateTransactionMutation,
  type SuggestCategorizationsQuery,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import Money from 'common/Money';
import {
  CalendarDays,
  ChevronDown,
  CreditCard,
  Ellipsis,
  Hash,
  PencilLine,
  Receipt,
  X,
} from 'lucide-react';
import {DateTime} from 'luxon';
import * as React from 'react';
import {AccountAvatar} from 'src/AccountAvatar';
import {AccountInlineDisplay} from 'src/AccountInlineDisplay';
import {AmountDisplay} from 'src/AmountDisplay';
import {addNewCategory} from 'src/apolloCache/updateCategories';
import {addPayee} from 'src/apolloCache/updatePayees';
import {addNewTags} from 'src/apolloCache/updateTags';
import {Button} from 'src/Button';
import {CategoryAvatar} from 'src/CategoryAvatar';
import {BankLine, DecisionPill, Expand, FromHistory, PayeeFace, ScreenFrame, SheetLine, SheetScreens, usePayeeLogos, valueClass, SheetInputLine} from 'src/CreateSheet/parts';
import {
  CategoryScreen,
  PayeeScreen,
  TransferTypeScreen,
  TransferTypeTile,
} from 'src/CreateSheet/screens';
import {
  BenefitPicker,
  benefitCoversDate,
  totalRemaining,
} from 'src/CreateTransactionForBenefitModal/BenefitPicker';
import DatePicker from 'src/DatePicker/DatePicker';
import {useMoneyFormatter} from 'src/hooks/useUnits';
import {inferTransferTypeFromAccounts} from 'src/inferTransferType';
import AmountInput from 'src/inputs/AmountInput';
import {
  AttachmentGhostChip,
  AttachmentPill,
} from 'src/inputs/TagsAndFieldsInput/AttachmentChips';
import {TagsAndFieldsInput} from 'src/inputs/TagsAndFieldsInput/TagsAndFieldsInput';
import {TextInput} from 'src/inputs/TextInput';
import Popover from 'src/Layer/Popover';
import {formatListDate} from 'src/lib/displayDate';
import {cn} from 'src/lib/utils';
import {Modal} from 'src/Modal';
import {PopoverMenuContext} from 'src/PopoverMenuContext';
import PopoverMenuItem from 'src/PopoverMenuItem';
import {TRANSFER_TYPE_LABELS} from 'src/TransferKindSelect';
import type {FetchResult} from '@apollo/client';
import type {Option} from '@danwang/option';
import type {AttachmentCandidate} from 'src/inputs/TagsAndFieldsInput/TagsAndFieldsInput';
import type {SystemProps} from 'src/Modals';
import type {PopoverMenuContextValue} from 'src/PopoverMenuContext';

type Suggestion = SuggestCategorizationsQuery['suggestCategorizations'][number];

// A top match covering at least this share of the merchant's history is
// auto-applied into untouched fields; anything less ambiguous stays a
// picker-only suggestion (sorted first in the payee/category dropdowns).
const AUTO_APPLY_RATIO = 0.6;

export type CreateTransactionModalUserProps = {
  onCreate: (data: {
    rawTransactionIds: string[];
    // Raw transactions that still have an unallocated remainder after a partial
    // allocation, carrying their updated remainingAmount. These should be kept
    // (and updated) in the inbox rather than removed.
    updatedRawTransactions?: BasicRawTransactionFragment[];
  }) => void;
  transactions: BasicRawTransactionFragment[];
  suggestion?: Suggestion | null;
  // Open with the benefit picker already expanded (the b hotkey's intent).
  // Ignored when the selection can't take a benefit.
  expandBenefit?: boolean;
};

type TransactionProperties = {
  payee: string;
  description: string;
  date: DateTime;
  category: Option<string>;
  tags: string[];
  fieldValues: Record<string, string>;
};

export const CreateTransactionModal = ({
  onRequestClose,
  onCreate,
  transactions,
  suggestion: externalSuggestion,
  expandBenefit,
}: CreateTransactionModalUserProps & SystemProps) => {
  const formId = React.useId();
  const formatMoney = useMoneyFormatter();
  // Field names for the tags line's summary (the tags screen asks the same).
  const {data: fieldsData} = useAllFieldsQuery();

  // History for a single row. A query rather than a fetch from an effect, so
  // an answer already cached (the Inbox card asks for every row it shows —
  // see suggestionsByRow) is there on the first render: the sheet opens
  // prefilled instead of flipping from empty once a round trip lands.
  const {data: fetchedData} = useSuggestCategorizationsQuery({
    variables: {rawTransactionIds: transactions.map((t) => t._id)},
    skip: externalSuggestion != null || transactions.length !== 1,
  });

  const suggestions: Suggestion[] = React.useMemo(() => {
    if (externalSuggestion) return [externalSuggestion];
    return fetchedData?.suggestCategorizations ?? [];
  }, [externalSuggestion, fetchedData]);

  const currency = transactions[0]?.currency ?? 'USD';

  const currencies = React.useMemo(
    () => [...new Set(transactions.map((t) => t.currency))],
    [transactions],
  );

  // Exactly two currencies is a conversion: the one transfer whose legs span
  // currencies (balanced by assertion — the legs define the exchange rate).
  const isConversion = currencies.length === 2;

  // Allocation math and the header total sum plain cent values and label them
  // with a single currency, so a selection across 3+ currencies can't be
  // represented coherently. Detect it and block submission rather than
  // silently treating it as one currency.
  const mixedCurrency = currencies.length > 2;

  // The still-unallocated (signed) amount of a raw transaction — its full amount
  // unless it was already partially consumed elsewhere.
  const remainingOf = (t: BasicRawTransactionFragment): number =>
    t.remainingAmount ?? t.amount;

  // Sum of the raws' still-unallocated (remaining) amounts. When this is zero
  // the selection is inherently a transfer regardless of how the user edits the
  // per-row allocations, which keeps the mode stable while editing a balanced
  // transfer's legs.
  const remainingNet = React.useMemo(
    () => transactions.reduce((sum, t) => sum + remainingOf(t), 0),
    [transactions],
  );

  const defaultAllocations = (): Record<string, Option<Money>> =>
    Object.fromEntries(
      transactions.map((t) => [
        t._id,
        some(Money.fromInt(Math.abs(remainingOf(t)), t.currency)),
      ]),
    );

  // Per-raw allocation amounts (absolute, positive). Each raw defaults to its
  // still-unallocated remaining amount; allocating less leaves a remainder in
  // the inbox. Allocation is now available for every raw transaction, enabling
  // multi-raw partial payments and partial transfers.
  const [allocations, setAllocations] = React.useState<
    Record<string, Option<Money>>
  >(defaultAllocations);

  // Reset allocations whenever the selected raw transactions change.
  React.useEffect(() => {
    setAllocations(defaultAllocations());
  }, [transactions]);

  const allocationAbs = (t: BasicRawTransactionFragment): number =>
    (allocations[t._id] ?? none).map((m) => Math.abs(m.amount)).getOrElse(0);

  const allocationError = (
    t: BasicRawTransactionFragment,
  ): 'empty' | 'over' | null => {
    const abs = allocationAbs(t);
    if (abs <= 0) return 'empty';
    if (abs > Math.abs(remainingOf(t))) return 'over';
    return null;
  };

  const allocationsValid = transactions.every(
    (t) => allocationError(t) === null,
  );

  // Signed sum of the allocated amounts. Drives the header readout and the
  // transfer net-zero requirement.
  const allocatedNet = transactions.reduce(
    (sum, t) => sum + Math.sign(t.amount) * allocationAbs(t),
    0,
  );

  // Treat the transaction as a balanced transfer when either the selection's
  // remaining amounts already net to zero (stable during editing), or the user
  // has allocated valid per-row amounts that net to zero. The latter makes
  // partial transfers reachable even when the full remaining amounts don't
  // balance (e.g. allocating $60 from a -$100 raw and a +$60 raw). A
  // conversion is always a transfer — its cross-currency sum is meaningless.
  const isBalanced =
    isConversion ||
    remainingNet === 0 ||
    (allocationsValid && allocatedNet === 0);

  // Signed allocated total per currency — the conversion's legs. Valid when
  // both legs are nonzero and run in opposite directions (mirrors the server
  // validation).
  const conversionLegs = (() => {
    if (!isConversion) return [];
    const byCurrency = new Map<string, number>();
    for (const t of transactions) {
      byCurrency.set(
        t.currency,
        (byCurrency.get(t.currency) ?? 0) +
          Math.sign(t.amount) * allocationAbs(t),
      );
    }
    // Outgoing leg first: reads as "from → to".
    return [...byCurrency.entries()].sort(([, a], [, b]) => a - b);
  })();

  // A conversion's foreign leg must live in ONE account: valuation charges the
  // whole home leg to every wallet a leg touches, so a leg split across two
  // wallets would double-count its cost. The server rejects it either way —
  // checking here keeps the modal from offering a Create that can only fail.
  const foreignLegSpansAccounts = React.useMemo(() => {
    const accountsByCurrency = new Map<string, Set<string>>();
    for (const t of transactions) {
      if (t.currency === 'USD') continue;
      const seen = accountsByCurrency.get(t.currency) ?? new Set<string>();
      seen.add(t.account._id);
      accountsByCurrency.set(t.currency, seen);
    }
    return [...accountsByCurrency.values()].some((s) => s.size > 1);
  }, [transactions]);

  // A foreign transfer carries its basis to ONE destination: the cost it draws
  // becomes a single lot, which cannot live in two accounts at once. Same
  // reason as above, and the server rejects it either way.
  const transferSpansDestinations = React.useMemo(() => {
    if (isConversion) return false;
    const destinations = new Set(
      transactions
        .filter((t) => t.currency !== 'USD' && t.amount > 0)
        .map((t) => t.account._id),
    );
    return destinations.size > 1;
  }, [transactions, isConversion]);

  const conversionLegsValid =
    isConversion &&
    conversionLegs.length === 2 &&
    conversionLegs.every(([, leg]) => leg !== 0) &&
    Math.sign(conversionLegs[0][1]) !== Math.sign(conversionLegs[1][1]) &&
    !foreignLegSpansAccounts;

  // Transfer-type inference: a credit card and a bank account moving money in
  // the payment direction. The selection already carries the amounts and each
  // account's depositoryType, so this needs no round-trip and lands before the
  // first paint. Every other shape is left to the user.
  //
  // Inferred from the *allocated* legs, the same signed amounts `allocatedNet`
  // balances on — not the raws' full amounts. A partial transfer (allocating
  // $60 from a -$100 raw against a +$60 one) and a raw already partly consumed
  // elsewhere both balance on allocations while their raw amounts do not, and
  // reading the raws would drop the type on exactly those.
  const suggestedTransferType = React.useMemo(
    () =>
      isConversion
        ? null
        : inferTransferTypeFromAccounts(
            transactions.map((t) => ({
              amount: Math.sign(t.amount) * allocationAbs(t),
              account: t.account,
            })),
          ),
    [transactions, isConversion, allocations],
  );

  // Null until the suggestion lands or the user picks: the select shows its
  // placeholder rather than a default that would silently label every
  // untouched transfer `account_transfer`.
  const [transferType, setTransferType] = React.useState<TransferType | null>(
    null,
  );
  const transferTypeManuallyChanged = React.useRef(false);

  // Reset transfer type when the selected transactions change. A two-currency
  // selection can only be a conversion, so lock the type to it.
  React.useEffect(() => {
    setTransferType(isConversion ? TransferType.Conversion : null);
    transferTypeManuallyChanged.current = false;
  }, [transactions, isConversion]);

  React.useEffect(() => {
    if (
      suggestedTransferType &&
      !transferTypeManuallyChanged.current &&
      !isConversion
    ) {
      setTransferType(suggestedTransferType);
    }
  }, [suggestedTransferType, isConversion]);

  const handleTransferTypeChange = (value: TransferType) => {
    transferTypeManuallyChanged.current = true;
    setTransferType(value);
  };

  // Payee deliberately does NOT prefill from the raw descriptor: the string
  // stays visible on the raw row below (with a copy affordance), so the
  // common case is typing a clean payee — or having the dominant historical
  // match auto-applied — rather than deleting a bank descriptor first.
  const [properties, setProperties] = React.useState<TransactionProperties>(
    () => ({
      payee: '',
      description: '',
      date:
        transactions.length > 0
          ? DateTime.min(...transactions.map((t) => parseDate(t.date)))
          : DateTime.local(),
      category: none,
      tags: [],
      fieldValues: {},
    }),
  );

  // Auto-apply the dominant historical match directly into the fields — the
  // suggestion card is gone, so a confident match lands as the prefill (with
  // sparkle provenance rendered by the inputs) and an ambiguous one surfaces
  // only through the suggestion-sorted pickers. One-shot: a response that
  // arrives after the user started typing must not overwrite their input,
  // and only untouched fields are filled.
  const autoApplyAttempted = React.useRef(false);
  const [autoApplied, setAutoApplied] = React.useState<{
    payee?: string;
    category?: string;
  } | null>(null);

  // Fields the user has started typing in, flagged on the first keystroke —
  // before the picker commits its text into `properties` on blur/submit. A
  // suggestion response landing mid-word would otherwise see the committed
  // value as untouched and overwrite the in-progress input.
  const editedFieldsRef = React.useRef({payee: false, category: false});

  // Before paint: with the answer already cached, the sheet's first frame
  // shows the prefill rather than "Add payee" for one frame.
  React.useLayoutEffect(() => {
    if (autoApplyAttempted.current || isBalanced || suggestions.length === 0) {
      return;
    }
    autoApplyAttempted.current = true;
    // Low-confidence matches are offered in the pickers, never prefilled.
    const confident = suggestions.filter(
      (s) => s.confidence === CategorizationConfidence.High,
    );
    if (confident.length === 0) return;
    const threshold = confident[0].totalCount * AUTO_APPLY_RATIO;
    // The payee first, by its total across categories (payeeMatchCount —
    // the server always sends that payee's best option): history that
    // agrees on the payee but splits on the category (one café, filed as
    // Dining at home and Travel on a trip) still names the payee. Then its
    // own best category, which fills only if it too clears the bar.
    const dominant = confident.reduce((best, s) =>
      s.payeeMatchCount > best.payeeMatchCount ? s : best,
    );
    if (dominant.payeeMatchCount < threshold) return;
    // Options arrive most frequent first.
    const top = confident.find((s) => s.payee === dominant.payee) ?? dominant;
    const bothAgree = top.matchCount >= threshold;
    const fillPayee =
      !editedFieldsRef.current.payee &&
      properties.payee === '' &&
      top.payee !== '';
    const fillCategory =
      bothAgree &&
      !editedFieldsRef.current.category &&
      properties.category.isEmpty &&
      top.categoryName !== '';
    if (!fillPayee && !fillCategory) return;
    setProperties((prev) => ({
      ...prev,
      payee: fillPayee ? top.payee : prev.payee,
      category: fillCategory ? some(top.categoryName) : prev.category,
    }));
    setAutoApplied({
      payee: fillPayee ? top.payee : undefined,
      category: fillCategory ? top.categoryName : undefined,
    });
  }, [suggestions, isBalanced, properties]);

  const handleChange =
    <K extends keyof TransactionProperties>(key: K) =>
    (value: TransactionProperties[K]) => {
      setProperties({...properties, [key]: value});
    };

  // ==================== Contextual attachments ====================
  // Benefit links and fee periods ride the Tags & Fields strip as contextual
  // sources. Both annotate exactly one raw and both are USD-only server-side
  // (linkBenefitToRawTransaction rejects non-USD raws; card-benefit amounts
  // are dollars throughout), and both belong to payments — so the sources
  // only offer themselves for a single USD raw in payment mode.
  const singleRaw = transactions.length === 1 ? transactions[0] : null;
  // Coarse shape gate — this feeds the query skip, which must not flap as
  // the user types in the allocation field.
  const attachmentsPossible =
    singleRaw != null && singleRaw.currency === 'USD' && !isBalanced;
  // Both attachments read the raw at face value — benefit usage sums the
  // whole linked raw's amount, and the fee period records the whole charge —
  // so this payment must consume the raw at face value: untouched by earlier
  // transactions, and allocated in full here. Either kind of shortfall (a
  // $20 allocation of a $100 raw, or a $100 raw that arrived with $50
  // already consumed elsewhere) would record a payment smaller than the
  // $100 the annotations claim.
  const attachmentsEligible =
    attachmentsPossible &&
    singleRaw != null &&
    remainingOf(singleRaw) === singleRaw.amount &&
    allocationAbs(singleRaw) === Math.abs(singleRaw.amount);

  // One query feeds both sources: the benefit list, and the raw-already-backs-
  // a-fee-period check. Skipped entirely for selections that can't attach.
  // This is the hot categorize path, so it's the lean AttachmentFeePeriods
  // selection (no transaction histories), not FullFeePeriod. no-cache rather
  // than network-only: freshness is required either way — a stale "no fee
  // period yet" lets the payment through only for the follow-up to fail on
  // the server's duplicate check — and bypassing the cache also keeps the
  // lean periods from overwriting the benefits views' fuller normalized
  // data.
  const {data: feePeriodsData, loading: feePeriodsLoading} =
    useAttachmentFeePeriodsQuery({
      skip: !attachmentsPossible,
      fetchPolicy: 'no-cache',
    });
  const allFeePeriods = feePeriodsData?.allFeePeriods;

  const [attachedBenefit, setAttachedBenefit] = React.useState<{
    benefit: AttachmentCardBenefitFragment;
    feePeriod: AttachmentFeePeriodFragment;
  } | null>(null);
  const [benefitPickerOpen, setBenefitPickerOpen] = React.useState(
    expandBenefit === true,
  );
  const benefitChipRef = React.useRef<HTMLButtonElement | null>(null);

  // The picker itself handles "none cover this date" (with a show-all
  // escape) and "no benefits at all", so the chip appears whenever any
  // benefit exists — and also while the picker is up because the b hotkey
  // requested it, so the shortcut surfaces the picker's loading and "No
  // benefits found" states instead of silently degrading to a plain create.
  const benefitSourceAvailable =
    attachmentsEligible &&
    (allFeePeriods ?? []).some((fp) => fp.benefits.length > 0);

  const rawDateSeconds =
    singleRaw != null ? parseDate(singleRaw.date).toSeconds() : 0;

  const attachBenefit = React.useCallback(
    (benefit: AttachmentCardBenefitFragment, feePeriod: AttachmentFeePeriodFragment) => {
      setAttachedBenefit({benefit, feePeriod});
      setBenefitPickerOpen(false);
      // The prefill the standalone benefit flow applied, with the same
      // overwrite-but-editable semantics as applySuggestion. Detaching the
      // benefit later never un-fills these.
      setProperties((prev) => ({
        ...prev,
        payee: feePeriod.payee ?? benefit.name,
        description: benefit.name,
        category: some(benefit.category.name),
      }));
    },
    [],
  );

  const [feePeriodDraft, setFeePeriodDraft] = React.useState<{
    payee: string;
    startDate: DateTime;
    endDate: DateTime;
  } | null>(null);

  // A fee period is keyed to a single annual-fee charge on a credit card, and
  // a raw that already backs one can't take another (the server rejects the
  // duplicate). Gated on the query having resolved so the chip can't flash in
  // before the already-linked check has data.
  const feePeriodSourceAvailable =
    attachmentsEligible &&
    singleRaw.amount !== 0 &&
    singleRaw.account.__typename === 'DepositoryAccount' &&
    singleRaw.account.depositoryType === DepositoryType.CreditCard &&
    allFeePeriods != null &&
    !allFeePeriods.some((fp) => fp.rawTransactionId === singleRaw._id);

  // The server rejects a period whose half-open end isn't after its start.
  // The draft's dates are inclusive (a same-day start/end is a valid one-day
  // period), so only end-before-start is invalid — caught here so the main
  // Create stays disabled, rather than letting the payment land and the
  // follow-up die on server validation.
  const feePeriodDraftInvalid =
    feePeriodSourceAvailable &&
    feePeriodDraft != null &&
    feePeriodDraft.endDate < feePeriodDraft.startDate;

  const [linkBenefit] = useLinkBenefitToRawTransactionMutation();
  const [createFeePeriod] = useCreateFeePeriodMutation();

  // Set when a follow-up write (benefit link, fee period) failed after the
  // transaction was already created — the terminal acknowledge-and-close
  // state rendered below.
  const [followUpError, setFollowUpError] = React.useState<string | null>(null);

  const attachmentPills = attachmentsEligible ? (
    <>
      {attachedBenefit != null && (
        <AttachmentPill
          icon={<CreditCard />}
          label={attachedBenefit.benefit.name}
          detail={`${formatMoney(
            Money.fromInt(totalRemaining(attachedBenefit.benefit), 'USD'),
          )} left`}
          onRemove={() => setAttachedBenefit(null)}
        />
      )}
      {(benefitSourceAvailable || benefitPickerOpen) &&
        attachedBenefit == null && (
          <AttachmentGhostChip
            ref={benefitChipRef}
            icon={<CreditCard />}
            label="Benefit"
            onClick={() => setBenefitPickerOpen((open) => !open)}
          />
        )}
      {feePeriodSourceAvailable && feePeriodDraft == null && (
        <AttachmentGhostChip
          icon={<Receipt />}
          label="Fee period"
          onClick={() => {
            const start = parseDate(singleRaw.date);
            setFeePeriodDraft({
              payee: singleRaw.account.name,
              startDate: start,
              endDate: start.plus({years: 1}).minus({days: 1}),
            });
          }}
        />
      )}
    </>
  ) : undefined;

  // The fee period needs its own sub-form (payee, period bounds), so once
  // attached it renders as a block under the strip rather than a pill.
  // The benefit picker opens right under the chip that toggles it; below
  // the tags screen's browse list it would land past the bottom of the
  // sheet, and the tap would look like it did nothing.
  const benefitPicker = (
    <Expand open={benefitPickerOpen && attachmentsEligible && attachedBenefit == null}>
      <div className="border-border rounded-xl border p-3">
        {feePeriodsLoading ? (
          <p className="text-muted-foreground text-sm">Loading benefits...</p>
        ) : (
          <BenefitPicker
            feePeriods={allFeePeriods ?? []}
            transactionDate={rawDateSeconds}
            onSelect={attachBenefit}
          />
        )}
      </div>
    </Expand>
  );

  const feePeriodBlock =
    feePeriodSourceAvailable && feePeriodDraft != null ? (
      <div className="border-border flex flex-col gap-2 rounded-md border px-3 py-2.5">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium">
            <Receipt className="text-muted-foreground size-3.5" />
            Fee period
          </span>
          <button
            type="button"
            onClick={() => setFeePeriodDraft(null)}
            className="text-muted-foreground hover:text-foreground cursor-pointer"
            title="Remove fee period"
          >
            <X className="size-3.5" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground w-12 shrink-0 text-xs">
            Payee
          </span>
          <TextInput
            value={feePeriodDraft.payee}
            onChange={(payee) => setFeePeriodDraft({...feePeriodDraft, payee})}
            placeholder="Card product name"
          />
        </div>
        <div className="flex items-start gap-2">
          <span className="text-muted-foreground w-12 shrink-0 pt-2 text-xs">
            Period
          </span>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            <DatePicker
              selectedDate={feePeriodDraft.startDate}
              onChange={(startDate) =>
                setFeePeriodDraft({...feePeriodDraft, startDate})
              }
            />
            <span className="text-muted-foreground">–</span>
            <DatePicker
              selectedDate={feePeriodDraft.endDate}
              onChange={(endDate) =>
                setFeePeriodDraft({...feePeriodDraft, endDate})
              }
            />
          </div>
        </div>
        {feePeriodDraftInvalid ? (
          <span className="text-destructive text-xs">
            End date is before the start date
          </span>
        ) : (
          <span className="text-muted-foreground text-xs">
            Created on submit, anchored to this charge
          </span>
        )}
      </div>
    ) : null;

  const attachmentBlocks = (
    <>
      {benefitPicker}
      {feePeriodBlock}
    </>
  );

  // Qualifying benefits also surface through the strip's type-ahead, so
  // typing part of a name attaches without reaching for the chip.
  const attachmentCandidates = React.useMemo((): AttachmentCandidate[] => {
    if (!benefitSourceAvailable || attachedBenefit != null || !allFeePeriods) {
      return [];
    }
    return allFeePeriods.flatMap((fp) =>
      fp.benefits
        .filter((b) => benefitCoversDate(b, rawDateSeconds))
        .map((b) => ({
          key: `benefit:${b._id}`,
          searchText: `${b.name} ${fp.payee ?? fp.account.name} benefit`,
          row: (
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <CreditCard className="text-muted-foreground size-3.5 shrink-0" />
              <span className="truncate font-medium">{b.name}</span>
              <span className="text-muted-foreground shrink-0 text-xs">
                {formatMoney(Money.fromInt(totalRemaining(b), 'USD'))} left ·
                benefit
              </span>
            </span>
          ),
          onAttach: () => attachBenefit(b, fp),
        })),
    );
  }, [
    benefitSourceAvailable,
    attachedBenefit,
    allFeePeriods,
    rawDateSeconds,
    formatMoney,
    attachBenefit,
  ]);

  const [createTransaction, {loading}] = useCreateTransactionMutation();

  // The mutation's own `loading` clears as soon as the create resolves, but a
  // payment's attachment follow-ups may still be in flight — and the raws are
  // already consumed, so a second Create in that window would double-spend
  // the allocation. This lock spans the whole submit, follow-ups included.
  const [submitting, setSubmitting] = React.useState(false);

  // For payment transactions, we need a category
  const paymentPayload = properties.category
    .map((category) => ({
      ...properties,
      category,
      rawTransactions: transactions.map((t) => t._id),
    }))
    .map((variables) => ({
      ...variables,
      date: variables.date.toISODate(),
    }));

  // A balanced (transfer) selection must keep its allocations netting to zero
  // and carry a type — the select starts empty, and submitting past it would
  // store an untyped transfer that no later reader can tell from one whose
  // type was considered and left off. A conversion balances by assertion
  // instead, needing two nonzero opposite-direction legs, and its type is
  // fixed. An unbalanced (payment) selection needs a payee (nothing
  // prefills one anymore, and the server rejects a payment without it), a
  // category, and a nonzero net — a zero-net payment is a balanced
  // transfer, which the server rejects when it carries payee/category, so
  // block that submit here. An attached
  // fee-period draft must also hold valid bounds: the payment mutation runs
  // before the fee-period follow-up, so a doomed draft would otherwise
  // allocate the raw and then fail terminally.
  const canSubmit =
    !mixedCurrency &&
    allocationsValid &&
    (isConversion
      ? conversionLegsValid
      : isBalanced
        ? allocatedNet === 0 &&
          !transferSpansDestinations &&
          transferType != null
        : !paymentPayload.isEmpty &&
          properties.payee.trim() !== '' &&
          allocatedNet !== 0 &&
          !feePeriodDraftInvalid);

  const pendingRef = React.useRef(false);

  // Per-raw allocation input sent to the mutation (one entry per raw).
  const allocationInput = transactions.map((t) => ({
    rawTransactionId: t._id,
    amount: allocationAbs(t),
  }));

  // Raws allocated below their remaining amount keep an unallocated remainder;
  // report them back so the inbox keeps them (with the updated remainingAmount)
  // instead of removing them.
  const remainderUpdate = (() => {
    const updated = transactions
      .filter((t) => allocationAbs(t) < Math.abs(remainingOf(t)))
      .map((t) => ({
        ...t,
        remainingAmount:
          remainingOf(t) - Math.sign(t.amount) * allocationAbs(t),
      }));
    return updated.length > 0 ? updated : undefined;
  })();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const pending = pendingRef.current;
    pendingRef.current = false;

    if (isBalanced) {
      // Create transfer transaction
      setSubmitting(true);
      void createTransaction({
        variables: {
          rawTransactions: transactions.map((t) => t._id),
          date: properties.date.toISODate(),
          description: properties.description,
          transferType,
          // Optional counterparty (a refund's merchant, a reimbursement's
          // payer) — omitted when the field was left empty.
          payee: properties.payee.trim() !== '' ? properties.payee : undefined,
          tags: properties.tags,
          fieldValues: properties.fieldValues,
          pending,
          allocations: allocationInput,
        },
        update: (store, result: FetchResult<CreateTransactionMutation>) => {
          const tx = result?.data?.createTransaction;
          if (tx?.payee) {
            addPayee(store, tx.payee);
          }
        },
      }).then((response) => {
        if (response.data) {
          onCreate({
            rawTransactionIds: response.data.createTransaction.consumptions.map(
              (c) => c.rawTransactionId,
            ),
            updatedRawTransactions: remainderUpdate,
          });
          onRequestClose();
        }
      }).finally(() => setSubmitting(false));
    } else {
      // Attachments are payment-only annotations; if edits flipped the
      // selection's shape since they were configured, they stand down (their
      // UI is hidden in those shapes too).
      const benefitToLink = attachmentsEligible ? attachedBenefit : null;
      const feePeriodToCreate =
        feePeriodSourceAvailable && feePeriodDraft != null
          ? feePeriodDraft
          : null;

      // Create payment transaction
      paymentPayload.forEach((variables) => {
        setSubmitting(true);
        void createTransaction({
          variables: {
            rawTransactions: variables.rawTransactions,
            payee: variables.payee,
            description: variables.description,
            date: variables.date,
            category: variables.category,
            tags: variables.tags,
            fieldValues: variables.fieldValues,
            pending,
            allocations: allocationInput,
          },
          update: (store, result: FetchResult<CreateTransactionMutation>) => {
            const tx = result?.data?.createTransaction;
            if (tx) {
              tx.category && addNewCategory(store, tx.category);
              tx.tags && addNewTags(store, tx.tags);
              tx.payee && addPayee(store, tx.payee);
            }
          },
        }).then(async (response) => {
          if (!response.data) return;
          // The raws are consumed regardless of how the follow-ups fare, so
          // report the allocation to the owning list first.
          onCreate({
            rawTransactionIds: response.data.createTransaction.consumptions.map(
              (c) => c.rawTransactionId,
            ),
            updatedRawTransactions: remainderUpdate,
          });

          // Attachment follow-ups run after the create; a failure keeps the
          // modal open saying what still needs doing by hand, instead of
          // silently closing (or blocking the created transaction).
          const failures: string[] = [];
          if (benefitToLink != null && singleRaw != null) {
            try {
              await linkBenefit({
                variables: {
                  rawTransactionId: singleRaw._id,
                  benefitId: benefitToLink.benefit._id,
                },
                refetchQueries: [
                  {
                    query: FeePeriodsByAccountDocument,
                    variables: {accountId: benefitToLink.benefit.accountId},
                  },
                ],
              });
            } catch {
              failures.push(
                'The benefit could not be linked — link it from the raw transaction page.',
              );
            }
          }
          if (feePeriodToCreate != null && singleRaw != null) {
            try {
              await createFeePeriod({
                variables: {
                  accountId: singleRaw.account._id,
                  rawTransactionId: singleRaw._id,
                  startDate: Math.floor(feePeriodToCreate.startDate.toSeconds()),
                  // The picker's end date is inclusive; stored bounds are
                  // half-open (same conversion as CreateFeePeriodModal)
                  endDate: Math.floor(
                    feePeriodToCreate.endDate.plus({days: 1}).toSeconds(),
                  ),
                  amount: Math.abs(singleRaw.amount),
                  payee: feePeriodToCreate.payee || undefined,
                },
                refetchQueries: [
                  {
                    query: FeePeriodsByAccountDocument,
                    variables: {accountId: singleRaw.account._id},
                  },
                  // Normalization updates existing entities everywhere, but
                  // can't add the new period to the cached root list that
                  // cache-first readers (the benefits overview) consume.
                  {query: AllFeePeriodsDocument},
                ],
              });
            } catch {
              failures.push(
                'The fee period could not be created — create it from the card benefits page.',
              );
            }
          }
          if (failures.length > 0) {
            setFollowUpError(failures.join(' '));
          } else {
            onRequestClose();
          }
        }).finally(() => setSubmitting(false));
      });
    }
  };

  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const closeDropdown = React.useCallback(() => setDropdownOpen(false), []);
  const toggleDropdown = React.useCallback(
    () => setDropdownOpen((s) => !s),
    [],
  );
  const formRef = React.useRef<HTMLFormElement | null>(null);

  const dropdownMenuContext = React.useMemo<PopoverMenuContextValue>(
    () => ({closeMenu: closeDropdown}),
    [closeDropdown],
  );

  const dropdownContent = (
    <PopoverMenuContext.Provider value={dropdownMenuContext}>
      <div className="shadow-popover bg-popover text-popover-foreground outline-ring/50 flex flex-col overflow-y-auto rounded-md outline-1">
        <PopoverMenuItem
          onSelect={() => {
            pendingRef.current = true;
            formRef.current?.requestSubmit();
          }}
        >
          Create as pending
        </PopoverMenuItem>
      </div>
    </PopoverMenuContext.Provider>
  );

  // ==================== The sheet ====================
  // One layout for every kind: a header (face, title, the one decision, the
  // amount), then lines for date, note, and tags and fields, then the bank
  // lines being used. Payee, category, transfer type, and tags and fields
  // each open as a second screen on the sheet.
  const payeeLogos = usePayeeLogos();
  type Screen = 'payee' | 'category' | 'type' | 'tags';
  const [screen, setScreen] = React.useState<Screen | null>(
    expandBenefit === true && attachmentsEligible ? 'tags' : null,
  );
  const [openLine, setOpenLine] = React.useState<'date' | null>(null);
  const toggleLine = (line: 'date') =>
    setOpenLine((current) => (current === line ? null : line));

  // What the bank said, before anyone changed it: the default the date line
  // reads quietly against.
  const initialDate = React.useMemo(
    () =>
      transactions.length > 0
        ? DateTime.min(...transactions.map((t) => parseDate(t.date)))
        : DateTime.local(),
    [transactions],
  );
  const dateEntered = !properties.date.hasSame(initialDate, 'day');

  // History's picks, for the screens' "This merchant" sections.
  const dedupe = (items: Array<{name: string; detail: string}>) => {
    const seen = new Set<string>();
    return items.filter((i) => i.name !== '' && !seen.has(i.name) && seen.add(i.name));
  };
  // A payee is counted across its categories, a category by its own option.
  const payeeDetailOf = (s: Suggestion) => `${s.payeeMatchCount} of ${s.totalCount}`;
  const detailOf = (s: Suggestion) => `${s.matchCount} of ${s.totalCount}`;
  const payeeSuggestions = dedupe(
    suggestions.map((s) => ({name: s.payee, detail: payeeDetailOf(s)})),
  );
  const categorySuggestions = dedupe(
    suggestions.map((s) => ({name: s.categoryName, detail: detailOf(s)})),
  );
  // The provenance of the payee actually filled in.
  const filledPayeeSuggestion = suggestions.find(
    (s) => s.payee === autoApplied?.payee,
  );
  const filledPayeeDetail =
    filledPayeeSuggestion != null ? payeeDetailOf(filledPayeeSuggestion) : undefined;

  // Picking by hand owns the field from then on (see editedFieldsRef), and
  // drops its "from history" mark.
  const pickPayee = (payee: string) => {
    editedFieldsRef.current.payee = true;
    setAutoApplied((prev) => (prev == null ? prev : {...prev, payee: undefined}));
    setProperties((prev) => ({...prev, payee}));
    setScreen(null);
  };
  const pickCategory = (category: string) => {
    editedFieldsRef.current.category = true;
    setAutoApplied((prev) => (prev == null ? prev : {...prev, category: undefined}));
    setProperties((prev) => ({...prev, category: some(category)}));
    setScreen(null);
  };

  // The accounts a transfer or conversion moves money between.
  const fromRaw = transactions.find((t) => t.amount < 0) ?? transactions[0];
  // The second face shows another account, even when the first landing row
  // shares the source's.
  const toRaw =
    transactions.find((t) => t.amount > 0 && t.account._id !== fromRaw?.account._id) ??
    transactions.find((t) => t.account._id !== fromRaw?.account._id) ??
    transactions[transactions.length - 1];
  // Every account a predicate's rows touch: a transfer may draw from, or
  // land in, several, and the header has to say so rather than name the first.
  const accountsWhere = (pick: (t: BasicRawTransactionFragment) => boolean) => [
    ...new Map(transactions.filter(pick).map((t) => [t.account._id, t.account.name])).values(),
  ];
  const sameAccount = new Set(transactions.map((t) => t.account._id)).size === 1;
  const sideLabel = (names: string[]) =>
    names.length === 1 ? names[0] : `${names.length} accounts`;
  const accountFaces = (
    <span className="relative size-10 shrink-0">
      {fromRaw && (
        <AccountAvatar account={fromRaw.account} className="absolute top-0 left-0 size-7 rounded-lg shadow-[0_0_0_2px_var(--background)]" />
      )}
      {toRaw && !sameAccount && (
        <AccountAvatar account={toRaw.account} className="absolute right-0 bottom-0 size-7 rounded-lg shadow-[0_0_0_2px_var(--background)]" />
      )}
    </span>
  );

  // ---- Header content by kind ----
  let face: React.ReactNode;
  let title: React.ReactNode;
  let sub: React.ReactNode;
  let amount: React.ReactNode;
  let amountDetail: React.ReactNode = null;
  let decision: React.ReactNode;
  if (!isBalanced) {
    face = (
      <PayeeFace name={properties.payee} logo={payeeLogos.get(properties.payee)} />
    );
    title = (
      <button
        type="button"
        onClick={() => setScreen('payee')}
        className="hover:bg-muted -mx-1.5 flex min-w-0 cursor-pointer items-start gap-1 rounded-lg px-1.5 text-left"
      >
        <span
          className={cn(
            'line-clamp-2 min-w-0 text-[17px] leading-tight font-semibold break-words',
            properties.payee === '' && 'text-muted-foreground font-medium',
          )}
        >
          {properties.payee === '' ? 'Add payee' : properties.payee}
        </span>
        <ChevronDown className="mt-0.5 size-3.5 shrink-0 text-blue-600 dark:text-blue-400" />
      </button>
    );
    sub =
      autoApplied?.payee != null && autoApplied.payee === properties.payee ? (
        <FromHistory detail={filledPayeeDetail} />
      ) : (
        transactions[0]?.account.name
      );
    amount = <AmountDisplay variant="plain" amount={Money.fromInt(allocatedNet, currency)} />;
    amountDetail =
      transactions.length > 1
        ? `${transactions.length} lines`
        : singleRaw != null && allocationAbs(singleRaw) !== Math.abs(remainingOf(singleRaw))
          ? `of ${formatMoney(Money.fromInt(remainingOf(singleRaw), singleRaw.currency))}`
          : null;
    const category = properties.category.getOrElse('');
    decision = (
      <DecisionPill
        onClick={() => setScreen('category')}
        leading={category !== '' ? <CategoryPillAvatar name={category} /> : undefined}
      >
        {category === '' ? (
          <span className="text-muted-foreground">Choose category</span>
        ) : (
          <>
            <span className={cn('truncate', autoApplied?.category === category ? '' : 'font-semibold')}>
              {category}
            </span>
            {autoApplied?.category === category && <FromHistory />}
          </>
        )}
      </DecisionPill>
    );
  } else if (isConversion) {
    const [outLeg, inLeg] = conversionLegs;
    face = accountFaces;
    title = (
      <span className="line-clamp-2 text-[17px] leading-tight font-semibold">
        To {sideLabel(accountsWhere((t) => t.currency === inLeg?.[0]))}
      </span>
    );
    sub = `From ${sideLabel(accountsWhere((t) => t.currency === outLeg?.[0]))}`;
    amount = inLeg ? formatMoney(Money.fromInt(inLeg[1], inLeg[0])) : null;
    amountDetail = outLeg ? formatMoney(Money.fromInt(Math.abs(outLeg[1]), outLeg[0])) : null;
    decision = (
      <DecisionPill fixed leading={<TransferTypeTile type={TransferType.Conversion} />}>
        {TRANSFER_TYPE_LABELS[TransferType.Conversion]}
      </DecisionPill>
    );
  } else {
    const moved = transactions
      .filter((t) => t.amount > 0)
      .reduce((sum, t) => sum + allocationAbs(t), 0);
    face = accountFaces;
    title = (
      <span className="line-clamp-2 text-[17px] leading-tight font-semibold">
        {sameAccount ? fromRaw?.account.name : `To ${sideLabel(accountsWhere((t) => t.amount > 0))}`}
      </span>
    );
    sub = sameAccount ? `${transactions.length} lines` : `From ${sideLabel(accountsWhere((t) => t.amount < 0))}`;
    amount = <AmountDisplay variant="plain" amount={Money.fromInt(moved, currency)} />;
    const suggestedApplied =
      transferType != null && transferType === suggestedTransferType && !transferTypeManuallyChanged.current;
    decision = (
      <DecisionPill onClick={() => setScreen('type')} leading={<TransferTypeTile type={transferType} />}>
        {transferType == null ? (
          <span className="text-muted-foreground">Choose type</span>
        ) : (
          <>
            <span className={cn('truncate', suggestedApplied ? '' : 'font-semibold')}>
              {TRANSFER_TYPE_LABELS[transferType]}
            </span>
            {suggestedApplied && <FromHistory />}
          </>
        )}
      </DecisionPill>
    );
  }

  // What stops Create, said where it's seen.
  const problem = mixedCurrency
    ? 'Pick lines in one currency, or in two for a conversion.'
    : isConversion && !conversionLegsValid
      ? foreignLegSpansAccounts
        ? 'One conversion per wallet — split this into separate conversions.'
        : 'The two sides must move in opposite directions.'
      : !isConversion && isBalanced && transferSpansDestinations
        ? 'One destination per transfer — split this into separate transfers.'
        : !isConversion && isBalanced && allocationsValid && allocatedNet !== 0
          ? `These lines are ${formatMoney(Money.fromInt(allocatedNet, currency))} off from netting to zero.`
          : null;

  const header = (
    <div className="flex flex-col gap-3 pb-3">
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5">
        <span className="row-span-2 flex">{face}</span>
        <span className="min-w-0">{title}</span>
        <Button
          variant="ghost"
          size="icon"
          onClick={onRequestClose}
          aria-label="Close"
          className="row-span-2 -mr-2 self-start"
        >
          <X className="size-4" />
        </Button>
        <span className="text-muted-foreground min-w-0 truncate text-[12.5px]">{sub}</span>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
        {decision}
        <span className="flex flex-col items-end">
          <span className="text-2xl font-semibold tracking-tight whitespace-nowrap tabular-nums">{amount}</span>
          {amountDetail != null && (
            <span className="text-muted-foreground text-[12.5px] whitespace-nowrap tabular-nums">{amountDetail}</span>
          )}
        </span>
      </div>
      {problem != null && (
        <div className="bg-destructive/10 text-destructive rounded-lg px-3 py-2 text-sm">{problem}</div>
      )}
    </div>
  );

  // ---- Lines ----
  const quickDates: Array<[string, DateTime]> = [
    [initialDate.toFormat('LLL d'), initialDate],
    ['Today', DateTime.local().startOf('day')],
    ['Yesterday', DateTime.local().startOf('day').minus({days: 1})],
  ];
  const fieldNames = new Map(
    (fieldsData?.allFields ?? []).map((field) => [field._id, field.name]),
  );
  const tagSummary: Array<{key: string; label: React.ReactNode}> = [
    ...properties.tags.map((t) => ({key: `tag:${t}`, label: t})),
    // Each field as it reads on the tags screen: its name, then its value.
    ...Object.entries(properties.fieldValues).map(([fieldId, value]) => ({
      key: `field:${fieldId}`,
      label: (
        <>
          <span className="text-muted-foreground">
            {fieldNames.get(fieldId) ?? 'Field'}:
          </span>{' '}
          {value}
        </>
      ),
    })),
    // Only what will actually be linked: an allocation edit can make the
    // selection ineligible, and submit then drops the benefit.
    ...(attachmentsEligible && attachedBenefit != null
      ? [{key: 'benefit', label: attachedBenefit.benefit.name}]
      : []),
    ...(feePeriodSourceAvailable && feePeriodDraft != null ? [{key: 'fee', label: 'Fee period'}] : []),
  ];
  const lines = (
    <div className="border-border divide-border border-y [&>*+*]:border-t">
      {isBalanced && (
        <SheetLine icon={<PayeeFace name={properties.payee} logo={payeeLogos.get(properties.payee)} className="size-5 rounded-md text-[10px]" />} onClick={() => setScreen('payee')} toScreen>
          {properties.payee === '' ? (
            <span className="text-muted-foreground">Add payee</span>
          ) : (
            <span className={valueClass(true)}>{properties.payee}</span>
          )}
        </SheetLine>
      )}
      <div>
        <SheetLine icon={<CalendarDays />} onClick={() => toggleLine('date')} open={openLine === 'date'}>
          <span className={valueClass(dateEntered)}>{properties.date.toFormat('LLL d, yyyy')}</span>
        </SheetLine>
        <Expand open={openLine === 'date'}>
          <div className="no-scrollbar flex items-center gap-2 overflow-x-auto">
            {quickDates.map(([label, date]) => (
              <button
                key={label}
                type="button"
                onClick={() => {
                  setProperties((prev) => ({...prev, date}));
                  setOpenLine(null);
                }}
                className={cn(
                  'border-border shrink-0 cursor-pointer rounded-full border px-3 py-1.5 text-sm',
                  properties.date.hasSame(date, 'day') && 'bg-primary text-primary-foreground border-primary',
                )}
              >
                {label}
              </button>
            ))}
            <div className="shrink-0">
              <DatePicker selectedDate={properties.date} onChange={handleChange('date')} />
            </div>
          </div>
        </Expand>
      </div>
      <SheetInputLine
        icon={<PencilLine />}
        value={properties.description}
        onChange={handleChange('description')}
        placeholder="Add a note"
        // Editing the note closes the date's shortcuts, as switching lines did.
        onFocus={() => setOpenLine(null)}
      />
      {/* Wraps rather than cutting off tags and fields that don't fit. */}
      <SheetLine icon={<Hash />} onClick={() => setScreen('tags')} toScreen wrap>
        {tagSummary.length === 0 ? (
          <span className="text-muted-foreground">Tags and fields</span>
        ) : (
          tagSummary.map((t) => (
            <span key={t.key} className="bg-muted border-border max-w-full truncate rounded-full border px-2 py-0.5 text-[13px] font-medium">
              {t.label}
            </span>
          ))
        )}
      </SheetLine>
    </div>
  );

  // ---- The bank lines being used ----
  const snapshots = React.useRef<Record<string, Option<Money>>>({});
  const bankLine = (t: BasicRawTransactionFragment) => {
    const error = allocationError(t);
    const remaining = Math.abs(remainingOf(t));
    const signed = Math.sign(t.amount) * allocationAbs(t);
    return (
      <BankLine
        key={t._id}
        description={t.description}
        account={<AccountInlineDisplay account={t.account} />}
        date={formatListDate(t.date)}
        display={<AmountDisplay variant="plain" amount={Money.fromInt(signed, t.currency)} />}
        partial={allocationAbs(t) !== remaining}
        detail={`of ${formatMoney(Money.fromInt(Math.sign(t.amount) * remaining, t.currency))}`}
        error={
          error === 'over'
            ? `At most ${formatMoney(Money.fromInt(remaining, t.currency))}`
            : error === 'empty'
              ? 'Enter an amount'
              : null
        }
        onOpen={() => {
          snapshots.current[t._id] = allocations[t._id] ?? none;
        }}
        onCancel={() =>
          setAllocations((prev) => ({...prev, [t._id]: snapshots.current[t._id] ?? prev[t._id]}))
        }
        onReset={() =>
          setAllocations((prev) => ({...prev, [t._id]: some(Money.fromInt(remaining, t.currency))}))
        }
        editor={() => (
          <AmountInput
            autoFocus
            value={allocations[t._id] ?? none}
            onChange={(value) => setAllocations((prev) => ({...prev, [t._id]: value}))}
            textAlign="right"
            currency={t.currency}
          />
        )}
      />
    );
  };
  const bankLines = (
    <div className="flex flex-col gap-1.5 pt-4">
      <div className="text-muted-foreground flex justify-between px-1 text-xs font-semibold tracking-wider uppercase">
        <span>{transactions.length === 1 ? 'From' : `${transactions.length} bank lines`}</span>
        <span>Using</span>
      </div>
      <div className="bg-muted/40 border-border overflow-hidden rounded-xl border">
        <div className="divide-border [&>*+*]:border-t">{transactions.map(bankLine)}</div>
      </div>
      {transactions.length > 1 && !isBalanced && (
        <div className="flex justify-between px-1 pt-1 text-sm font-semibold">
          <span>Total</span>
          <AmountDisplay variant="plain" amount={Money.fromInt(allocatedNet, currency)} />
        </div>
      )}
    </div>
  );

  // ---- Second screens ----
  const renderScreen = (key: string) => {
    if (key === 'payee') {
      return (
        <PayeeScreen
          title="Payee"
          value={properties.payee}
          suggested={isBalanced ? [] : payeeSuggestions}
          logos={payeeLogos}
          onPick={isBalanced ? (payee) => {
            setProperties((prev) => ({...prev, payee}));
            setScreen(null);
          } : pickPayee}
          // A transfer's payee is optional, so it can be taken away again.
          onClear={isBalanced && properties.payee !== '' ? () => {
            setProperties((prev) => ({...prev, payee: ''}));
            setScreen(null);
          } : undefined}
          onBack={() => setScreen(null)}
        />
      );
    }
    if (key === 'category') {
      return (
        <CategoryScreen
          value={properties.category.getOrElse('') || null}
          suggested={categorySuggestions}
          onPick={pickCategory}
          onBack={() => setScreen(null)}
        />
      );
    }
    if (key === 'type') {
      return (
        <TransferTypeScreen
          value={transferType}
          suggested={suggestedTransferType}
          onPick={(type) => {
            handleTransferTypeChange(type);
            setScreen(null);
          }}
          onBack={() => setScreen(null)}
        />
      );
    }
    return (
      <ScreenFrame
        title="Tags and fields"
        onBack={() => setScreen(null)}
        trailing={
          <button type="button" onClick={() => setScreen(null)} className="cursor-pointer text-sm font-semibold text-blue-600 dark:text-blue-400">
            Done
          </button>
        }
      >
        <TagsAndFieldsInput
          variant="screen"
          tags={properties.tags}
          fieldValues={properties.fieldValues}
          onChange={(tags, fieldValues) => setProperties((prev) => ({...prev, tags, fieldValues}))}
          attachmentPills={attachmentPills}
          attachmentBlocks={attachmentBlocks}
          attachmentCandidates={attachmentCandidates}
        />
      </ScreenFrame>
    );
  };

  const footer = (
    <div className="flex w-full gap-2">
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={loading || submitting || !canSubmit}
        className="flex-1 py-3 text-base"
      >
        Create
      </Button>
      <Popover
        open={dropdownOpen}
        popoverContents={dropdownContent}
        popoverAnchor="above-right"
        onOutsideClick={closeDropdown}
      >
        {(anchorRef) => (
          <Button
            ref={anchorRef}
            variant="outline"
            disabled={loading || submitting || !canSubmit}
            onClick={toggleDropdown}
            aria-label="More ways to create"
            className="px-3"
          >
            <Ellipsis className="size-4" />
          </Button>
        )}
      </Popover>
    </div>
  );

  // A follow-up write failed after the transaction was created: the raws are
  // consumed and the list already updated, so the only path forward is
  // acknowledging what still needs doing by hand. Mirrors the terminal state
  // the standalone benefit flow had.
  if (followUpError != null) {
    return (
      <Modal
        onRequestClose={onRequestClose}
        header="Transaction created"
        footer={
          <Button variant="primary" onClick={onRequestClose}>
            Close
          </Button>
        }
      >
        <p className="text-destructive text-sm">{followUpError}</p>
      </Modal>
    );
  }

  return (
    <Modal
      label="New transaction"
      onRequestClose={onRequestClose}
      // On a phone, a sheet as tall as what it holds rather than the whole
      // screen; a centered dialog from sm up, as before.
      className="top-auto bottom-0 h-auto max-h-[92dvh] rounded-b-none sm:rounded-b-lg"
      footer={screen == null ? footer : undefined}
    >
      <form
        id={formId}
        ref={formRef}
        // A second screen has no Create: Enter in its search box must not
        // submit the transaction behind it.
        onSubmit={(event) => {
          if (screen != null) {
            event.preventDefault();
            return;
          }
          handleSubmit(event);
        }}
      >
        <SheetScreens
          screen={screen}
          renderScreen={renderScreen}
          main={
            <>
              {header}
              {lines}
              {bankLines}
            </>
          }
        />
      </form>
    </Modal>
  );
};

/** A category's avatar, sized for the decision pill. */
const CategoryPillAvatar = ({name}: {name: string}) => {
  const {data} = useAllCategoriesQuery();
  const category = data?.allCategories.find((c) => c.name === name) ?? {name};
  return <CategoryAvatar category={category} className="size-6 rounded-md max-md:size-6" />;
};
